import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { BUILTIN_TEMPLATES, type BuiltinTemplate } from '@lumo/shared';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/cn';
import { useBoards, useProjects } from '@/lib/queries';
import { BoardCard } from './BoardCard';
import { useCreateBoard } from './BoardsPage';
import { useDashboard } from './DashboardLayout';
import { TemplatePreview } from './TemplatePreview';

import { tr } from '@/i18n';
export function TemplateCard({ tpl, onPick, compact }: { tpl: BuiltinTemplate; onPick: () => void; compact?: boolean }) {
  const content = useMemo(() => tpl.build(), [tpl]);
  const reduce = useReducedMotion();
  return (
    <motion.button
      type="button"
      onClick={onPick}
      whileHover={reduce ? undefined : { y: -2 }}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      className={cn('group flex shrink-0 flex-col overflow-hidden rounded-panel bg-surface text-left shadow-card transition-shadow hover:shadow-float focus-visible:ring-2 focus-visible:ring-accent', compact ? 'w-44' : 'w-full')}
    >
      <TemplatePreview content={content} className="aspect-[16/10] w-full border-b border-border" />
      <span className="px-3 py-2.5">
        <span className="block truncate text-[13px] font-medium">{tr(tpl.name)}</span>
        {!compact && <span className="mt-0.5 line-clamp-2 block text-xs text-fg-subtle">{tr(tpl.description)}</span>}
      </span>
    </motion.button>
  );
}

/** Галерея шаблонов: встроенные по категориям + шаблоны воркспейса. */
export function TemplatesPage() {
  const { workspace } = useDashboard();
  const create = useCreateBoard();
  const projects = useProjects(workspace.id);
  const custom = useBoards({ view: 'templates', workspaceId: workspace.id });
  const categories = [...new Set(BUILTIN_TEMPLATES.map((t) => t.category))];
  const [cat, setCat] = useState<string | null>(null);
  const list = BUILTIN_TEMPLATES.filter((t) => !cat || t.category === cat);
  const canCreate = workspace.role !== 'guest';

  return (
    <div className="mx-auto w-full max-w-7xl px-5 py-7 sm:px-8">
      <h1 className="text-2xl font-semibold tracking-tight">{tr("Шаблоны")}</h1>
      <p className="mt-0.5 text-sm text-fg-subtle">{tr("Готовые структуры для ретро, планирования, исследований и схем.")}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {[null, ...categories].map((c) => (
          <button
            key={c ?? 'all'}
            type="button"
            aria-pressed={cat === c}
            onClick={() => setCat(c)}
            className={cn('rounded-full px-3 py-1.5 text-xs font-medium transition-colors', cat === c ? 'bg-accent text-white' : 'bg-surface text-fg-muted shadow-card hover:text-fg')}
          >
            {c ? tr(c) : tr("Все")}
          </button>
        ))}
      </div>

      <section className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-5">
        {list.map((tpl) => (
          <TemplateCard key={tpl.id} tpl={tpl} onPick={() => canCreate && void create({ workspaceId: workspace.id, templateId: tpl.id, name: tpl.name })} />
        ))}
      </section>

      <section className="mt-10">
        <h2 className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">{tr("Шаблоны воркспейса")}</h2>
        {custom.data?.length ? (
          <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-5">
            {custom.data.map((b, i) => (
              <div key={b.id} className="flex flex-col gap-2">
                <BoardCard board={b} projects={projects.data ?? []} index={i} />
                {canCreate && (
                  <button type="button" onClick={() => void create({ workspaceId: workspace.id, templateId: b.id, name: b.name })} className="rounded-control bg-accent-soft py-1.5 text-xs font-medium text-accent-fg hover:brightness-95">
                    {tr("Создать доску из шаблона")}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title={tr("Своих шаблонов пока нет")} text={tr("Откройте доску и выберите в меню «Сохранить как шаблон» — команда сможет начинать с неё.")} />
        )}
      </section>
    </div>
  );
}
